import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Users, ClipboardList, ListChecks, CalendarDays, AlertTriangle,
  Lightbulb, Building2, Scissors, TrendingUp, Award, ShieldCheck,
  RefreshCw, ArrowDownRight, ArrowUpRight,
  FileText, FolderOpen, ScrollText, Bell, Presentation, Layers, Gauge,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon + gradient per report_index tab
const TAB_STYLE = {
  index1:         { icon: ClipboardList, gradient: 'from-blue-800 to-blue-950',    shadow: 'shadow-blue-800/30' },
  index2:         { icon: ListChecks,    gradient: 'from-indigo-600 to-indigo-800', shadow: 'shadow-indigo-600/30' },
  daily_work:     { icon: CalendarDays,  gradient: 'from-cyan-500 to-sky-600',      shadow: 'shadow-cyan-500/30' },
  daily_schedule: { icon: CalendarDays,  gradient: 'from-amber-600 to-orange-700',  shadow: 'shadow-amber-600/30' },
  work_problem:   { icon: AlertTriangle, gradient: 'from-rose-500 to-pink-600',     shadow: 'shadow-rose-500/30' },
  suggestion:     { icon: Lightbulb,     gradient: 'from-teal-500 to-emerald-600',  shadow: 'shadow-teal-500/30' },
  company_dev:    { icon: Building2,     gradient: 'from-violet-500 to-purple-700', shadow: 'shadow-violet-500/30' },
  cost_cutting:   { icon: Scissors,      gradient: 'from-fuchsia-500 to-pink-700',  shadow: 'shadow-fuchsia-500/30' },
};

// Green ≥75, amber ≥40, red below
const badgeTone = (pct) => {
  if (pct >= 75) return { cls: 'bg-emerald-50 border-emerald-200 text-emerald-700', up: true };
  if (pct >= 40) return { cls: 'bg-amber-50 border-amber-200 text-amber-700', up: false };
  return { cls: 'bg-red-50 border-red-200 text-red-700', up: false };
};

const KpiBadge = ({ pct, total }) => {
  if (!total) {
    return (
      <div className="absolute top-3 right-3 rounded-lg px-2 py-0.5 bg-white/20 text-white/90 text-[10px] font-semibold">
        No data
      </div>
    );
  }
  const tone = badgeTone(pct);
  const Arrow = tone.up ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`absolute top-3 right-3 rounded-lg px-2.5 py-1.5 shadow-lg flex items-center gap-1 border ${tone.cls}`}>
      <Arrow className="w-3.5 h-3.5" />
      <span className="text-sm font-extrabold leading-none">{pct}%</span>
    </div>
  );
};

// Generic gradient stat card for the "Other Tabs" overview (value shown directly, no badge).
const StatCard = ({ icon: Icon, label, value, sub, gradient, shadow, testid }) => (
  <Card
    className={`bg-gradient-to-br ${gradient} border-0 shadow-xl ${shadow} hover:scale-[1.02] transition-transform duration-200`}
    data-testid={testid}
  >
    <CardContent className="p-5 min-h-[140px] flex flex-col justify-between">
      <div className="flex items-start space-x-2">
        <div className="p-2.5 bg-white/20 rounded-lg flex-shrink-0"><Icon className="w-5 h-5 text-white" /></div>
        <h3 className="text-white/95 text-sm font-semibold leading-tight">{label}</h3>
      </div>
      <div>
        <p className="text-4xl font-bold text-white tracking-tight mb-1">{value}</p>
        <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium">{sub}</p>
      </div>
    </CardContent>
  </Card>
);

// PART 2 — "Other Tabs" overview (HR Reports, Employees, Staff forms, SOP, Circular, PPT, Project Stage)
const OtherTabsPanel = ({ data }) => {
  const ot = data.otherTabs || {};
  const sfd = ot.staffFormsDetails || {};
  const pctText = (v) => (v === null || v === undefined ? '—' : `${v}%`);

  // Static reference tabs (no completion metric — mirror the tab content)
  const referenceTabs = [
    { label: 'SOP', icon: ScrollText, sub: 'Standard procedures' },
    { label: 'Circular', icon: Bell, sub: 'Notices & policies' },
    { label: 'Presentation PPT', icon: Presentation, sub: 'Training decks' },
    { label: 'Project Work Stage', icon: Layers, sub: 'Stage tracker' },
  ];

  return (
    <div className="space-y-6" data-testid="hr-overview-others">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="hr-others-kpi-ribbon">
        <StatCard testid="hr-other-employees" icon={Users} label="Employees"
          value={Number(ot.employees || 0).toLocaleString()} sub="Total staff"
          gradient="from-blue-600 to-blue-800" shadow="shadow-blue-600/30" />
        <StatCard testid="hr-other-performance" icon={TrendingUp} label="Performance"
          value={pctText(ot.performancePct)} sub="Employees rated Done"
          gradient="from-green-600 to-green-800" shadow="shadow-green-600/30" />
        <StatCard testid="hr-other-skills" icon={Award} label="Skills"
          value={pctText(ot.skillPct)} sub="Skill checklist done"
          gradient="from-orange-500 to-rose-600" shadow="shadow-orange-500/30" />
        <StatCard testid="hr-other-docs" icon={ShieldCheck} label="Document Compliance"
          value={pctText(ot.documentPct)} sub="Setup docs OK"
          gradient="from-slate-600 to-slate-800" shadow="shadow-slate-600/30" />
        <StatCard testid="hr-other-staff-advance" icon={Gauge} label="Staff Advance"
          value={pctText(ot.staffAdvancePct)} sub={`${ot.staffAdvanceFilled || 0} cells filled`}
          gradient="from-amber-500 to-yellow-600" shadow="shadow-amber-500/30" />
        <StatCard testid="hr-other-staff-forms-details" icon={ClipboardList} label="Staff Forms Details"
          value={pctText(sfd.pct)} sub={sfd.total ? `${sfd.done} / ${sfd.total} filled` : 'No forms yet'}
          gradient="from-cyan-500 to-teal-600" shadow="shadow-cyan-500/30" />
        <StatCard testid="hr-other-staff-forms" icon={FolderOpen} label="Staff Form Templates"
          value={Number(ot.staffFormTemplates || 0).toLocaleString()} sub="Templates in library"
          gradient="from-indigo-500 to-indigo-700" shadow="shadow-indigo-500/30" />
      </div>

      {/* Reference-only tabs (no completion metric) */}
      <div data-testid="hr-others-reference">
        <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Reference Tabs</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {referenceTabs.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.label} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 transition-colors"
                data-testid={`hr-other-ref-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className="p-2 rounded-lg bg-stone-100 flex-shrink-0"><Icon className="w-4 h-4 text-stone-600" /></div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-stone-700 truncate">{r.label}</p>
                  <p className="text-[11px] text-stone-400 truncate">{r.sub}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const HROverviewTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [part, setPart] = useState('index'); // 'index' | 'others'

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/hr/reports/dashboard-overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setData(null);
      toast.error('Failed to load HR overview. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="hr-overview-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-stone-400">Could not load overview data.</div>;
  }

  const cards = data.cards || [];
  const hr = data.hrReports || {};

  // Chart: Done vs In Process vs Remaining per tab
  const chartData = cards.map(c => ({
    name: c.label.length > 12 ? c.label.split(' ')[0] : c.label,
    fullName: c.label,
    Done: c.done,
    'In Process': c.inProcess,
    Remaining: Math.max(0, c.total - c.done - c.inProcess),
  }));

  const hrReportCards = [
    { key: 'perf', label: 'Performance', value: hr.performancePct, icon: TrendingUp, gradient: 'from-green-600 to-green-800', shadow: 'shadow-green-600/30', sub: 'Employees rated Done' },
    { key: 'skill', label: 'Skills', value: hr.skillPct, icon: Award, gradient: 'from-orange-500 to-rose-600', shadow: 'shadow-orange-500/30', sub: 'Skill checklist done' },
    { key: 'doc', label: 'Document Compliance', value: hr.documentPct, icon: ShieldCheck, gradient: 'from-slate-600 to-slate-800', shadow: 'shadow-slate-600/30', sub: 'Setup docs OK' },
  ];

  return (
    <div className="space-y-6" data-testid="hr-overview-tab">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-stone-800">HR Overview</h2>
          <p className="text-sm text-stone-500">
            {part === 'index'
              ? 'Part 1 — Index 1 & 2 report completion · all-time'
              : 'Part 2 — Other tabs (HR Reports, Employees, Staff Forms, SOP, Circular, PPT, Project Stage)'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {part === 'index' && (
            <div className="text-right">
              <p className="text-2xl font-extrabold text-emerald-700 leading-none" data-testid="hr-overall-pct">{data.overallPct}%</p>
              <p className="text-[11px] text-stone-500 uppercase tracking-wide">Overall Completion</p>
            </div>
          )}
          <button
            onClick={fetchOverview}
            data-testid="hr-overview-refresh"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Two-part toggle */}
      <div className="flex gap-1 bg-stone-100 rounded-xl p-1 w-fit" data-testid="hr-overview-part-toggle">
        {[
          { id: 'index', label: 'Index 1 & 2' },
          { id: 'others', label: 'Other Tabs' },
        ].map(p => (
          <button
            key={p.id}
            onClick={() => setPart(p.id)}
            data-testid={`hr-overview-part-${p.id}`}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${part === p.id ? 'bg-white text-indigo-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {part === 'others' && <OtherTabsPanel data={data} />}

      {part === 'index' && (
      <>
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="hr-overview-kpi-ribbon">
        {/* Total Staff — no % badge */}
        <Card className="bg-gradient-to-br from-stone-700 to-stone-900 border-0 shadow-xl shadow-stone-700/30 hover:scale-[1.02] transition-transform duration-200" data-testid="hr-kpi-total-staff">
          <CardContent className="p-5 min-h-[140px] flex flex-col justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-2.5 bg-white/20 rounded-lg"><Users className="w-5 h-5 text-white" /></div>
              <h3 className="text-white/95 text-sm font-semibold">Total Staff</h3>
            </div>
            <div>
              <p className="text-4xl font-bold text-white tracking-tight mb-1">{Number(data.totalStaff || 0).toLocaleString()}</p>
              <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium">Employees</p>
            </div>
          </CardContent>
        </Card>

        {/* Report-index tab cards */}
        {cards.map((c) => {
          const style = TAB_STYLE[c.key] || { icon: ClipboardList, gradient: 'from-slate-500 to-slate-700', shadow: 'shadow-slate-500/30' };
          const Icon = style.icon;
          return (
            <Card
              key={c.key}
              className={`bg-gradient-to-br ${style.gradient} border-0 shadow-xl ${style.shadow} hover:scale-[1.02] transition-transform duration-200`}
              data-testid={`hr-kpi-${c.key}`}
            >
              <CardContent className="p-5 min-h-[140px] flex flex-col justify-between relative">
                <div className="flex items-start space-x-2 pr-16">
                  <div className="p-2.5 bg-white/20 rounded-lg flex-shrink-0"><Icon className="w-5 h-5 text-white" /></div>
                  <h3 className="text-white/95 text-sm font-semibold leading-tight">{c.label}</h3>
                </div>
                <div>
                  <p className="text-4xl font-bold text-white tracking-tight mb-1">{Number(c.done).toLocaleString()}</p>
                  <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium">Done · of {Number(c.total).toLocaleString()}</p>
                </div>
                <KpiBadge pct={c.pct} total={c.total} />
              </CardContent>
            </Card>
          );
        })}

        {/* HR Reports % cards — value is the % itself */}
        {hrReportCards.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.key}
              className={`bg-gradient-to-br ${c.gradient} border-0 shadow-xl ${c.shadow} hover:scale-[1.02] transition-transform duration-200`}
              data-testid={`hr-kpi-${c.key}`}
            >
              <CardContent className="p-5 min-h-[140px] flex flex-col justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-2.5 bg-white/20 rounded-lg"><Icon className="w-5 h-5 text-white" /></div>
                  <h3 className="text-white/95 text-sm font-semibold">{c.label}</h3>
                </div>
                <div>
                  <p className="text-4xl font-bold text-white tracking-tight mb-1">{c.value ?? 0}%</p>
                  <p className="text-[11px] text-white/85 uppercase tracking-wider font-medium">{c.sub}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Compiled summary chart */}
      <Card data-testid="hr-overview-summary-chart">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="w-5 h-5 text-blue-700" />
            Compiled Report Index Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n, p) => [v, n]} labelFormatter={(l, p) => p?.[0]?.payload?.fullName || l} />
              <Legend />
              <Bar dataKey="Done" stackId="a" fill="#16a34a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="In Process" stackId="a" fill="#f59e0b" />
              <Bar dataKey="Remaining" stackId="a" fill="#e5e7eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Compiled table */}
      <Card data-testid="hr-overview-summary-table">
        <CardHeader>
          <CardTitle className="text-base">All Tabs — Completion Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-800 text-white">
                  <th className="px-4 py-2.5 text-left font-semibold">TAB</th>
                  <th className="px-4 py-2.5 text-right font-semibold">DONE</th>
                  <th className="px-4 py-2.5 text-right font-semibold">IN PROCESS</th>
                  <th className="px-4 py-2.5 text-right font-semibold">TOTAL</th>
                  <th className="px-4 py-2.5 text-right font-semibold">COMPLETION %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {cards.map((c) => {
                  const tone = badgeTone(c.pct);
                  return (
                    <tr key={c.key} className="hover:bg-stone-50" data-testid={`hr-overview-row-${c.key}`}>
                      <td className="px-4 py-2.5 font-medium text-stone-700">{c.label}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-emerald-700">{c.done.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-amber-600">{c.inProcess.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-stone-600">{c.total.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">
                        {c.total === 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-400">No data</span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border ${tone.cls}`}>{c.pct}%</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-stone-100 font-bold">
                  <td className="px-4 py-2.5 text-stone-800">OVERALL</td>
                  <td className="px-4 py-2.5 text-right text-emerald-800">{data.grandDone.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right">—</td>
                  <td className="px-4 py-2.5 text-right text-stone-800">{data.grandTotal.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right text-stone-900">{data.overallPct}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default HROverviewTab;
